// The "fill in the blanks" layer from the design review: everything here is per-peer presentation
// riding events and state the client already has, plus one new message (pings). No sim rules.
//   - callouts: chains, team plays (cross-class detonations), multikills, lives, achievements
//   - sound the bank had but nothing played: sniper charge + laser, empty click, overheat, boss roar
//   - per-class firing rumble, kill / headshot / shield / down rumble
//   - pings and a quick-chat wheel (tap / hold Z or D-pad up)
//   - Pop's first-session radio teach beats (coach.ts), a local profile + achievements (profile.ts)
//   - the end-of-run Paperwork card (paperwork.ts) from stats counted off the event stream (stats.ts)
// GameClient calls onEvent / onPredicted / frame; nothing else in the client depends on this.

import { Ev, GameEvent, MarkKind } from '../../sim/codec';
import { CLASSES, ClassId, ENEMIES, EType } from '../../sim/defs';
import { nemesisName } from '../../sim/nemesis';
import { RunPhase } from '../../sim/run';
import { weaponDef } from '../../sim/weapons';
import { Btn, InputFrame, Phase, PlayerState, StepOut } from '../../sim/player';
import { EFlag, NetEnemy, RemotePlayer } from '../../sim/protocol';
import type { HudCallout, HudMarker, HudState } from '../render/contracts';
import type { Input } from '../input/input';
import type { Sfx } from '../audio/sfx';
import type { NetSession } from '../net/session';
import { Coach } from './coach';
import { AchievementDef, grant, loadProfile, Profile, saveProfile } from './profile';
import { RunStats } from './stats';
import { showPaperwork } from './paperwork';

/** What the layer reads from the GameClient each frame. */
export interface ReviewHost {
  sfx: Sfx;
  input: Input;
  session: NetSession;
  roster: Map<number, { id: number; name: string; slot: number; cls: number; on: boolean }>;
  notice(text: string): void;
  enemies(): { list: readonly NetEnemy[]; n: number };
  players(): { list: readonly RemotePlayer[]; n: number };
  /** where the crosshair lands in the world, and the robot under it */
  aim(): { x: number; y: number; z: number; eid: number };
  project(x: number, y: number, z: number, out: HudMarker): void;
}

/** Quick-chat lines, in wheel order: up, right, down, left. Index + 1 is the MarkKind. */
export const WHEEL_LINES = ['PRIME IT!', 'DETONATE!', 'NEED A HAND', 'YEE-HAW!'] as const;
const WHEEL_HOLD = 0.28;
const PING_SECS = 6;

/** Firing rumble per class: [strong, weak, ms]. Gunslinger kicks; the automatics purr. */
const SHOT_RUMBLE: Record<number, [number, number, number]> = {
  [ClassId.Gunslinger]: [0.35, 0.5, 70],
  [ClassId.Valkyrie]: [0.0, 0.22, 34],
  [ClassId.Phantom]: [0.18, 0.4, 55],
  [ClassId.Juggernaut]: [0.12, 0.3, 40],
};

const MULTI = ['', '', 'DOUBLE DISCOUNT', 'TRIPLE MARKDOWN', 'CLEARANCE SALE', 'EVERYTHING MUST GO'];
const MULTI_WINDOW = 1.5;

export class ReviewLayer {
  profile: Profile = loadProfile();
  stats = new RunStats();
  coach: Coach;
  private callouts: { text: string; sub: string; tone: HudCallout['tone']; at: number }[] = [];
  /** cowboys who just went down: the server's automatic help mark follows */
  private autoHelp = new Set<number>();
  /** posted outlaws still standing (enemy ids), for the Product Recall achievement */
  private outlaws = new Set<number>();
  /** friendly-fire callouts, throttled per shooter→victim pair */
  private ffAt = new Map<number, number>();
  private pings = new Map<number, { kind: number; x: number; y: number; z: number; eid: number; at: number; slot: number; name: string }>();
  private time = 0;
  private filedThisRun: AchievementDef[] = [];
  // multikill
  private killTimes: number[] = [];
  // ping / wheel
  private pingDownAt = -1;
  private wheelSel = -1;
  // sniper telegraphs we're voicing
  private sniping = new Set<number>();
  private seenSnipers = new Set<number>();
  // edges
  private lastLives = -1;
  private lastDir = -1;
  private wasFiring = false;
  private hotWave = { on: false, ok: true, wave: 0 };
  private lastMarkAt = -9;
  private lastHeatFrac = 0;
  private bossRoared = -1;

  constructor(private host: ReviewHost) {
    this.coach = new Coach(this.profile, () => {
      const me = host.session.me;
      const c = CLASSES[me?.cls ?? 0];
      return { primer: c.primer, detonator: c.detonator, primerGlyph: me?.cls === ClassId.Valkyrie ? 'ability' : 'melee' };
    });
    this.stats.reset(performance.now());
  }

  private get myId() { return this.host.session.pid; }
  private nameOf(id: number) { return (this.host.roster.get(id)?.name ?? 'SOMEBODY').toUpperCase(); }

  call(text: string, sub = '', tone: HudCallout['tone'] = 'phos') {
    // the same call twice in a row refreshes instead of stacking
    const last = this.callouts[this.callouts.length - 1];
    if (last && last.text === text) { last.sub = sub; last.at = this.time; return; }
    this.callouts.push({ text, sub, tone, at: this.time });
    if (this.callouts.length > 3) this.callouts.shift();
  }

  private file(id: string) {
    const a = grant(this.profile, id);
    if (!a) return;
    this.filedThisRun.push(a);
    this.call(a.name.toUpperCase(), 'ACHIEVEMENT FILED · ' + a.desc.toUpperCase(), 'amber');
    this.host.sfx.play('wave_clear', undefined, { volume: 0.5, pitch: 1.25 });
  }

  // ------------------------------------------------------------------ server events
  /** The run state machine (sim/run.ts) speaks Ev.Island / Ev.Run / Ev.RunEnd; the review layer was written
   *  against the old director's Ev.Wave [wave, state 0 calm / 1 live / 2 cleared, tier]. Translate. */
  private runTier = 0;
  private legacyWave(e: GameEvent): GameEvent | null {
    const f = e.f;
    if (e.t === Ev.Island) {
      this.runTier = f[2] * 2 + (f[3] >= 1 ? 1 : 0);
      return f[2] === 0 && f[3] === 0 ? { t: Ev.Wave, f: [0, 0, 0] } : null; // a fresh run
    }
    if (e.t === Ev.Run) {
      const [phase, ticks, wave] = f;
      if (phase === RunPhase.Waves) return ticks > 0 ? { t: Ev.Wave, f: [wave - 1, 0, this.runTier] } : { t: Ev.Wave, f: [wave, 1, this.runTier] };
      if (phase === RunPhase.GateOpen) return { t: Ev.Wave, f: [wave, 2, this.runTier] };
      return null;
    }
    if (e.t === Ev.RunEnd && f[0] === 0) { this.endRun('extracted'); this.hotWave.on = false; }
    return null;
  }

  onEvent(e: GameEvent) {
    const lw = this.legacyWave(e);
    if (lw) this.onEvent(lw);
    const f = e.f, me = this.myId, h = this.host;
    // read who primed it before the stats forget the robot
    const primer = e.t === Ev.Detonate ? this.stats.primer(f[0]) : -1;
    this.stats.onEvent(e);
    switch (e.t) {
      case Ev.Detonate: {
        const [, pid, , , , , , , cross] = f;
        if (pid === me) { this.file('first_det'); if (this.host.roster.get(me)?.cls === ClassId.Phantom) this.coach.want('cloak_phantom', 8, true); }
        if (cross) {
          const mine = pid === me || primer === me;
          if (mine) {
            this.call('TEAM PLAY', `${this.nameOf(primer)} PRIMED · ${this.nameOf(pid)} DETONATED · ×3 BURST`, 'prime');
            h.sfx.play('kill', undefined, { volume: 0.7, pitch: 1.5 });
            h.input.rumble(0.4, 0.8, 120);
            if (pid === me) this.file('team_det');
            const myLine = this.stats.line(me);
            if (myLine.assists >= 5) this.file('assist5');
          } else h.notice(`TEAM PLAY · ${this.nameOf(primer)} → ${this.nameOf(pid)}`);
        }
        // the last unit standing, with the rest of the posse on the floor
        if (pid === me && this.host.enemies().n <= 1) {
          const others = this.host.players();
          let alive = 0, total = 0;
          for (let i = 0; i < others.n; i++) { const p = others.list[i]; if (!(this.host.roster.get(p.id)?.on ?? true)) continue; total++; if (p.phase === Phase.Alive) alive++; }
          if (total > 0 && alive === 0) this.file('pyrrhic');
        }
        break;
      }
      case Ev.Chain: {
        const [pid, n] = f;
        if (pid === me && n >= 3) { this.call(`CHAIN ×${n}`, n >= 5 ? 'ACCELERATED DEPRECIATION' : 'KEEP IT MOVING', 'prime'); h.sfx.play('prime', undefined, { pitch: 1 + Math.min(0.6, n * 0.08), volume: 0.6 }); }
        else if (pid !== me && n >= 5) h.notice(`${this.nameOf(pid)} CHAINED ×${n}`);
        if (pid === me && n >= 5) this.file('chain5');
        break;
      }
      case Ev.Nemesis: {
        const [eid, nameIdx, , victim] = f;
        this.outlaws.add(eid);
        const sub = victim !== 255 && h.roster.has(victim) ? `RECALL NOTICE · FOR DOWNING ${this.nameOf(victim)}` : 'RECALL NOTICE POSTED';
        this.call(`WANTED: ${nemesisName(nameIdx)}`, sub, 'red');
        h.sfx.play('brute_roar', undefined, { volume: 0.5, pitch: 1.4 });
        h.input.rumble(0.35, 0.2, 180);
        break;
      }
      case Ev.EnemyDie: {
        const [eid, type, , , , killer] = f;
        if (this.outlaws.delete(eid) && killer === me) this.file('nemesis');
        if (killer !== me) break;
        h.input.rumble(0.15, 0.35, 60);
        if (type === EType.MetalBrute) this.file('metal');
        this.killTimes.push(this.time);
        while (this.killTimes.length && this.time - this.killTimes[0] > MULTI_WINDOW) this.killTimes.shift();
        const n = this.killTimes.length;
        if (n >= 2) {
          this.call(MULTI[Math.min(n, MULTI.length - 1)], `${n} UNITS PROCESSED`, n >= 4 ? 'amber' : 'phos');
          h.sfx.play('kill', undefined, { pitch: 1 + n * 0.1, volume: 0.55 });
        }
        if (n >= 5) this.file('multi5');
        break;
      }
      case Ev.EnemyHit: if (f[3] === me && (f[2] & 1)) h.input.rumble(0, 0.55, 35); break;
      case Ev.Lives: {
        const n = f[0];
        if (this.lastLives >= 0 && n < this.lastLives) {
          h.sfx.play('down', undefined, { pitch: 0.6, volume: 0.5 });
          if (n === 1) this.call('LAST LIFE', 'THE POSSE HAS ONE REDEPLOY LEFT', 'red');
          else if (n === 0) this.call('NO LIVES LEFT', 'NEXT DOWN IS PERMANENT', 'red');
        } else if (this.lastLives >= 0 && n > this.lastLives) this.call(`+${n - this.lastLives} POSSE LIVES`, `${n} REDEPLOYS ON FILE`, 'amber');
        this.lastLives = n;
        break;
      }
      case Ev.Blink: if (f[0] === me) this.coach.did('blink_phantom'); break;
      case Ev.Special: {
        const pid = f[0];
        if (pid === me) { const c = this.host.roster.get(me)?.cls; this.coach.did(c === ClassId.Valkyrie ? 'move_valkyrie' : c === ClassId.Phantom ? 'move_phantom' : 'move_juggernaut'); }
        if (pid !== me) {
          const p = this.findPlayer(pid);
          if (p) h.sfx.play('dodge', { x: p.x, y: p.y + 1, z: p.z }, { pitch: 0.7, volume: 0.7 });
        }
        break;
      }
      case Ev.PlayerHurt: {
        const [pid, , , , flags] = f;
        if (pid === me && (flags & 1)) h.input.rumble(0.6, 0.2, 240);
        break;
      }
      case Ev.Down: {
        if (f[0] === me) { h.input.rumble(1, 1, 380); this.coach.want('self_down'); }
        else this.coach.want('teammate_down');
        this.autoHelp.add(f[0]); // the server's NEED A HAND mark rides right behind this
        break;
      }
      case Ev.Revive: if (f[1] === me) { const l = this.profile; l.revives++; saveProfile(l); if (l.revives >= 10) this.file('revive10'); } break;
      case Ev.Wave: {
        const [wave, state] = f;
        if (state === 0 && wave === 0) this.newRun();
        if (state === 1 && wave === 1) this.coach.want('inbound');
        if (state === 1 && wave >= 6 && this.bossRoared !== wave) { this.bossRoared = wave; h.sfx.play('brute_roar', undefined, { volume: 0.9 }); }
        if (state === 1) this.hotWave = { on: true, ok: this.lastHeatFrac >= 0.8, wave };
        // only a wave that was actually cleared counts (a wipe's reset to wave 0 doesn't)
        if (state !== 1 && this.hotWave.on) { if (this.hotWave.ok && wave >= 1 && wave === this.hotWave.wave) this.file('heat'); this.hotWave.on = false; }
        if (state === 0 && wave >= 1) this.coach.want('ping');
        if (state === 1 && wave >= 2) this.coach.want('grenade', 40);
        if (state === 2) this.coach.want('cleared');
        break;
      }
      case Ev.Mark: {
        const [pid, kind, x, y, z, eid] = f;
        const ro = h.roster.get(pid);
        if (kind === MarkKind.Ping) {
          this.pings.set(pid, { kind, x, y, z, eid, at: this.time, slot: ro?.slot ?? 0, name: (ro?.name ?? '?').toUpperCase() });
          h.sfx.play('notice', undefined, { pitch: 1.35, volume: 0.6 });
        } else {
          h.notice(`${this.nameOf(pid)}: ${WHEEL_LINES[kind - 1] ?? ''}`);
          h.sfx.play(kind === MarkKind.YeeHaw ? 'wave_start' : 'notice', undefined, { pitch: kind === MarkKind.YeeHaw ? 1.3 : 1.15, volume: 0.6 });
        }
        // the automatic help call on going down doesn't count as using the radio
        const auto = kind === MarkKind.NeedHand && this.autoHelp.delete(pid);
        if (pid === me && !auto) this.file('ping');
        break;
      }
      case Ev.FriendlyHit: {
        const [victim, by, dmg, downed] = f;
        if (by === victim) {
          // your own blast found you: a cook-off, or standing too close to the Bad Idea
          if (by === me) { this.file('self_harm'); if (this.throttle(by * 256 + victim, 2.5)) this.call('SELF-INFLICTED', 'THE FUSE WAS NOT A SUGGESTION', 'red'); }
          break;
        }
        if (downed) {
          h.notice(`${this.nameOf(victim)} DOWNED BY ${this.nameOf(by)}. HR NOTIFIED.`);
          if (by === me) this.file('ff_down');
        }
        if (by === me) {
          this.coach.want('friendly', 10, true);
          if (this.throttle(by * 256 + victim, 2.5) || downed) {
            this.call('FRIENDLY FIRE', `${this.nameOf(victim)} FILED A COMPLAINT`, 'red');
            h.sfx.play('denied', undefined, { volume: 0.7 });
            h.input.rumble(0.4, 0.2, 90);
          }
        } else if (victim === me && dmg >= 1 && this.throttle(by * 256 + victim, 2.5)) {
          h.notice(`${this.nameOf(by)} HIT YOU`);
        }
        break;
      }
      case Ev.Crate: {
        const [, wid, , , , on, taker] = f;
        if (on) { this.coach.want('crate', 45, true); break; }
        if (taker === 255) break; // reclaimed: the arsenal already lets it go quietly
        const w = weaponDef(wid);
        if (taker === me) {
          this.call(w.name.toUpperCase(), 'ORDNANCE REQUISITIONED', 'amber');
          h.sfx.play('ui_select', undefined, { volume: 0.8 });
          h.input.rumble(0.3, 0.5, 120);
          this.coach.want('special', 20, true);
          this.file('crate');
        } else h.notice(`${this.nameOf(taker)} TOOK THE ${w.name.toUpperCase()}`);
        break;
      }
      case Ev.Turret: {
        const owner = f[5];
        if (owner === me) this.call('SENTRY ON THE CLOCK', 'IT SHOOTS WHAT YOU SHOOT AT. MOSTLY.', 'phos');
        else h.notice(`${this.nameOf(owner)} SET A SENTRY`);
        break;
      }
      case Ev.Throw: if (f[0] === me) this.coach.want('cook', 30); break;
      case Ev.BulletSpawn: {
        // the sniper's slug gets its own crack instead of a pitched-down pistol shot
        const [, kind, , x, y, z] = f;
        if (kind === 1 /* BKind.Slug */) h.sfx.play('sniper_fire', { x, y, z });
        break;
      }
    }
  }

  /** true at most once per `secs` for a key (callout spam guard) */
  private throttle(key: number, secs: number) {
    const at = this.ffAt.get(key) ?? -99;
    if (this.time - at < secs) return false;
    this.ffAt.set(key, this.time);
    return true;
  }

  private findPlayer(id: number) {
    const ps = this.host.players();
    for (let i = 0; i < ps.n; i++) if (ps.list[i].id === id) return ps.list[i];
    return null;
  }

  private newRun() {
    // a fresh contract: lifetime totals fold in when the run ends (endRun), not here
    this.stats.reset(performance.now());
    this.filedThisRun = [];
    this.outlaws.clear(); this.ffAt.clear();
  }

  /** The run is over for the whole posse (wipe now; Ev.RunEnd later). */
  endRun(result: 'wiped' | 'extracted') {
    const me = this.myId;
    const mine = this.stats.line(me);
    const p = this.profile;
    p.runs++; p.kills += mine.kills; p.detonations += mine.detonations; p.teamDets += mine.teamDets; p.assists += mine.assists;
    p.downs += mine.downs; p.bestWave = Math.max(p.bestWave, this.stats.waves); p.bestChain = Math.max(p.bestChain, mine.bestChain);
    saveProfile(p);
    const awards = this.stats.awards();
    if (awards[0]?.id === me) this.file('eom');
    const cowboys = [...this.host.roster.values()].map((r) => ({ id: r.id, name: r.name, slot: r.slot }));
    showPaperwork(result, this.stats, cowboys, this.filedThisRun, me);
  }

  // ------------------------------------------------------------------ predicted (own cowboy)
  onPredicted(p: PlayerState, inp: InputFrame, out: StepOut) {
    const h = this.host;
    if (out.shots) { const r = SHOT_RUMBLE[p.cls]; if (r && p.cls !== ClassId.Gunslinger) h.input.rumble(r[0], r[1], r[2]); }
    const firing = (inp.buttons & Btn.Fire) !== 0;
    if (firing && !this.wasFiring && p.mag === 0 && p.reloadT === 0) { h.sfx.play('empty'); this.coach.want('swap', 15); }
    this.wasFiring = firing;
  }

  // ------------------------------------------------------------------ per frame
  frame(dt: number, time: number, me: PlayerState | null, s: HudState) {
    this.time = time;
    const h = this.host;
    if (me) {
      const cd = CLASSES[me.cls];
      const heat = me.heat / Math.max(1, cd.heatMax);
      if (heat >= 0.995 && this.lastHeatFrac < 0.995) { h.sfx.play('overheat'); this.coach.want('overheat'); }
      if (this.hotWave.on && heat < 0.8) this.hotWave.ok = false;
      this.lastHeatFrac = heat;
      if (me.mag <= Math.ceil(cd.weapon.mag * 0.25) && me.phase === Phase.Alive) this.coach.want('reload');
      if (s.aimType > 0 && !s.aimPrimed) this.coach.want('prime');
      if (s.aimPrimed) this.coach.want('primed');
      // the class move, then the class's held strike, once a robot's in front of him (round 2: James never found the charge)
      if (s.aimType > 0 && me.phase === Phase.Alive) {
        if (me.cls === ClassId.Valkyrie) { this.coach.want('move_valkyrie', 30); if (!s.grounded && me.shield > 0) this.coach.want('pound_valkyrie', 5); }
        else if (me.cls === ClassId.Phantom) { this.coach.want('move_phantom', 30); if (s.aimDist < 4.5) this.coach.want('blink_phantom', 6, true); }
        else if (me.cls === ClassId.Juggernaut) { this.coach.want('move_juggernaut', 30); if (s.aimPrimed) this.coach.want('hammer_juggernaut', 20); }
      }
    }
    // wipe: everybody sees their Paperwork together
    const dir = h.session.latest?.dirState ?? -1;
    if (dir === 3 && this.lastDir !== 3 && this.lastDir !== -1) { this.endRun('wiped'); this.lastLives = -1; this.hotWave.on = false; }
    this.lastDir = dir;
    this.sniperAudio();
    this.comms(dt);
    this.coach.update(time);
    this.fillHud(s);
  }

  /** The sniper's 1.4 s windup is the deadliest telegraph in the game: charge whine + laser hum. */
  private sniperAudio() {
    const { list, n } = this.host.enemies();
    const seen = this.seenSnipers; seen.clear();
    for (let i = 0; i < n; i++) {
      const e = list[i];
      if (e.type !== EType.Sniper || e.hp <= 0 || e.st !== 1) continue;
      seen.add(e.id);
      const pos = { x: e.x, y: e.y + 1.6, z: e.z };
      if (!this.sniping.has(e.id)) { this.sniping.add(e.id); this.host.sfx.play('sniper_charge', pos); }
      this.host.sfx.loop('snp' + e.id, 'sniper_laser', 0.9, pos, 0.9 + e.prog * 0.5);
    }
    for (const id of this.sniping) if (!seen.has(id)) { this.sniping.delete(id); this.host.sfx.loop('snp' + id, 'sniper_laser', 0); }
  }

  /** Tap to ping what's under the crosshair; hold to open the wheel, point the move stick, let go. */
  private comms(dt: number) {
    void dt;
    const inp = this.host.input;
    const held = inp.pingHeld;
    if (held && this.pingDownAt < 0) this.pingDownAt = this.time;
    const holding = held && this.pingDownAt >= 0 && this.time - this.pingDownAt >= WHEEL_HOLD;
    inp.wheelOpen = holding;
    if (holding) {
      const x = inp.wheelX, y = inp.wheelY;
      if (Math.hypot(x, y) > 0.5) {
        const a = Math.atan2(x, y); // 0 = up, +pi/2 = right
        this.wheelSel = ((Math.round(a / (Math.PI / 2)) % 4) + 4) % 4;
      }
    }
    if (!held && this.pingDownAt >= 0) {
      const wasWheel = this.time - this.pingDownAt >= WHEEL_HOLD;
      const a = this.host.aim();
      // the server takes one mark per 0.6 s; say no here rather than let it vanish
      if (this.time - this.lastMarkAt < 0.62) this.host.sfx.play('denied');
      else {
        this.lastMarkAt = this.time;
        // a wheel let go without picking a line is still a ping (the centre reads PING)
        if (!wasWheel || this.wheelSel < 0) this.host.session.mark(MarkKind.Ping, a.x, a.y, a.z, a.eid);
        else this.host.session.mark(this.wheelSel + 1, a.x, a.y, a.z, 0);
      }
      this.pingDownAt = -1; this.wheelSel = -1;
      inp.wheelOpen = false;
    }
  }

  private fillHud(s: HudState) {
    const out: HudCallout[] = s.callouts ?? (s.callouts = []);
    out.length = 0;
    this.callouts = this.callouts.filter((c) => this.time - c.at < 2.6);
    for (const c of this.callouts) out.push({ text: c.text, sub: c.sub, tone: c.tone, age: this.time - c.at });
    s.wheel = { open: this.host.input.wheelOpen, sel: this.wheelSel, lines: WHEEL_LINES };
    s.coach = this.coach.hud(this.time);
    // pings ride the marker list: on a robot they follow it, otherwise they sit where they landed
    const { list, n } = this.host.enemies();
    for (const [pid, pg] of this.pings) {
      const age = this.time - pg.at;
      if (age > PING_SECS) { this.pings.delete(pid); continue; }
      let x = pg.x, y = pg.y + 0.4, z = pg.z, onRobot = false;
      if (pg.eid) for (let i = 0; i < n; i++) if (list[i].id === pg.eid) { x = list[i].x; y = list[i].y + 2.4; z = list[i].z; onRobot = list[i].hp > 0; break; }
      if (pg.eid && !onRobot && age > 0.5) { this.pings.delete(pid); continue; }
      // the slot colour says who; the label stays short so it fits the marker's redraw box
      const mk = { kind: 'ping', t: age, slot: pg.slot, label: pg.name.slice(0, 8) } as HudMarker;
      this.host.project(x, y, z, mk);
      s.markers.push(mk);
    }
    // the outlaw wears a red WANTED pin for as long as it's standing
    for (let i = 0; i < n; i++) {
      const e = list[i];
      if (!(e.flags & EFlag.Nemesis) || e.hp <= 0) continue;
      const mk = { kind: 'ping', t: 1, slot: -1, label: 'WANTED' } as HudMarker;
      this.host.project(e.x, e.y + 2.4 + (ENEMIES[e.type]?.radius ?? 0), e.z, mk);
      s.markers.push(mk);
    }
  }
}
